import React, { useEffect, useState } from "react";
import { X, Truck, FileText } from "lucide-react";
import api, { img, money } from "../../lib/api";
import { AdminHeader } from "./AdminLayout";
import { toast } from "sonner";

const STATUSES = ["pending", "processing", "packed", "shipped", "out_for_delivery", "delivered", "cancelled", "returned"];
const PILL = { paid: "bg-moss/20 text-moss", created: "bg-beige text-forest/60", failed: "bg-brown/20 text-brown" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("");
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/orders${filter ? `?payment=${filter}` : ""}`).then(r => setOrders(r.data));
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (status) => {
    const r = await api.put(`/admin/orders/${sel.id}`, { fulfillment_status: status });
    setSel(r.data); load(); toast.success(`Marked ${status.replace(/_/g, " ")}`);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const r = await api.put(`/admin/orders/${sel.id}`, { note });
    setSel(r.data); setNote(""); toast.success("Note added");
  };
  const createShipment = async () => {
    try { const r = await api.post(`/admin/orders/${sel.id}/ship`); toast.success(r.data.mode === "mock" ? "Demo shipment created (Shiprocket not connected)" : "Shiprocket shipment created");
      const o = await api.get(`/admin/orders`); setOrders(o.data); setSel(o.data.find(x => x.id === sel.id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create shipment"); }
  };

  return (
    <div>
      <AdminHeader title="Orders" subtitle={`${orders.length} orders`} action={
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-sage/40 bg-white px-3 py-2 text-sm rounded-sm" data-testid="order-filter">
          <option value="">All Payments</option><option value="paid">Paid</option><option value="created">Unpaid</option><option value="failed">Failed</option>
        </select>} />

      <div className="bg-white border border-sage/30 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="border-b border-sage/30 text-left text-forest/50 text-xs uppercase tracking-wide">
            <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Fulfilment</th><th className="p-3">Date</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setSel(o)} className="border-b border-sage/15 hover:bg-beige/40 cursor-pointer" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="p-3 font-medium">{o.order_number}</td>
                <td className="p-3">{o.customer?.name}<div className="text-xs text-forest/45">{o.customer?.email}</div></td>
                <td className="p-3">{money(o.total)}</td>
                <td className="p-3"><span className={`text-xs px-2 py-1 ${PILL[o.payment.status] || "bg-beige"}`}>{o.payment.status}</span></td>
                <td className="p-3 capitalize">{o.fulfillment_status.replace(/_/g, " ")}</td>
                <td className="p-3 text-forest/50">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-forest/40" onClick={() => setSel(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-ivory overflow-auto p-6" data-testid="admin-order-detail">
            <div className="flex items-center justify-between mb-6"><div><div className="serif text-3xl">{sel.order_number}</div><div className="text-xs text-forest/50">{new Date(sel.created_at).toLocaleString("en-IN")}</div></div><button onClick={() => setSel(null)}><X size={22} /></button></div>

            <div className="bg-beige/40 p-4 mb-5 text-sm">
              <div className="font-medium">{sel.customer?.name}</div>
              <div className="text-forest/60">{sel.customer?.email} · {sel.customer?.phone}</div>
              <div className="text-forest/60 mt-2">{sel.shipping_address?.address}, {sel.shipping_address?.city}, {sel.shipping_address?.state} {sel.shipping_address?.pincode}</div>
            </div>

            <div className="divide-y divide-sage/20 mb-5">
              {sel.items.map((it) => (
                <div key={it.slug} className="flex gap-3 py-3"><img src={img(it.image)} alt="" className="h-14 w-12 object-cover bg-beige/40" /><div className="flex-1 text-sm"><div className="serif text-lg">{it.name}</div><div className="text-xs text-forest/50">Qty {it.quantity} · {money(it.price)}</div></div><span className="text-sm">{money(it.price * it.quantity)}</span></div>
              ))}
            </div>
            <div className="space-y-1 text-sm mb-6 border-t border-sage/30 pt-3">
              <div className="flex justify-between text-forest/60"><span>Subtotal</span><span>{money(sel.subtotal)}</span></div>
              {sel.discount > 0 && <div className="flex justify-between text-moss"><span>Discount</span><span>− {money(sel.discount)}</span></div>}
              <div className="flex justify-between text-forest/60"><span>Shipping</span><span>{sel.shipping ? money(sel.shipping) : "Free"}</span></div>
              <div className="flex justify-between serif text-xl pt-2"><span>Total</span><span>{money(sel.total)}</span></div>
              <div className="text-xs text-forest/50 pt-1">Payment: {sel.payment.status} {sel.payment.razorpay_payment_id ? `· ${sel.payment.razorpay_payment_id}` : ""}</div>
            </div>

            <div className="mb-6">
              <div className="eyebrow text-forest/50 mb-2">Fulfilment</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => updateStatus(s)} disabled={sel.payment.status !== "paid"}
                    className={`text-xs px-3 py-1.5 border capitalize disabled:opacity-30 ${sel.fulfillment_status === s ? "bg-forest text-ivory border-forest" : "border-sage/40 hover:border-forest"}`} data-testid={`status-${s}`}>{s.replace(/_/g, " ")}</button>
                ))}
              </div>
              <button onClick={createShipment} disabled={sel.payment.status !== "paid"} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-30" data-testid="create-shipment"><Truck size={15} /> Create Shipment</button>
              {sel.shipment?.awb && <div className="text-xs text-forest/60 mt-2 bg-beige/40 p-3">AWB {sel.shipment.awb} · {sel.shipment.courier} · {sel.shipment.status}</div>}
            </div>

            <div className="mb-4">
              <div className="eyebrow text-forest/50 mb-2">Timeline</div>
              <div className="space-y-2 text-xs">
                {(sel.timeline || []).map((t, i) => <div key={i} className="flex justify-between"><span className="text-forest">{t.status}</span><span className="text-forest/40">{new Date(t.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>)}
              </div>
            </div>

            <div>
              <div className="eyebrow text-forest/50 mb-2">Internal Notes</div>
              {(sel.internal_notes || []).map((n, i) => <div key={i} className="text-xs bg-beige/40 p-2 mb-1">{n.note} <span className="text-forest/40">— {n.by}</span></div>)}
              <div className="flex gap-2 mt-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="flex-1 border border-sage/40 bg-white px-3 py-2 text-sm" data-testid="order-note-input" />
                <button onClick={addNote} className="bg-forest text-ivory px-4 text-xs uppercase">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
